import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminShell } from "@/components/AdminShell";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, LogOut, Search, ExternalLink, Users, Mail, MessageSquare, Calendar, RefreshCcw } from "lucide-react";

export default function SuperAdmin() {
  const { user, ready, logout } = useAuth();
  const [overview, setOverview] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("events"); // events | organisers
  const navigate = useNavigate();

  useEffect(() => {
    if (!ready) return;
    if (!user || !user.is_super_admin) {
      navigate("/admin", { replace: true });
    }
  }, [ready, user, navigate]);

  async function load() {
    setLoading(true);
    try {
      const [ov, o] = await Promise.all([
        api.get("/super-admin/overview"),
        api.get(`/super-admin/organisers${search ? `?search=${encodeURIComponent(search)}` : ""}`),
      ]);
      setOverview(ov.data);
      setOrgs(o.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load super-admin data");
    } finally { setLoading(false); }
  }

  useEffect(() => { if (user?.is_super_admin) load(); /* eslint-disable-next-line */ }, [user?.id]);

  useEffect(() => {
    // Debounced search on organisers
    if (!user?.is_super_admin) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/super-admin/organisers${search ? `?search=${encodeURIComponent(search)}` : ""}`);
        setOrgs(data || []);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [search, user?.id]);

  const totals = useMemo(() => {
    if (!overview) return { sms: 0, email: 0, vols: 0, events: 0 };
    return overview.events.reduce((acc, e) => ({
      sms: acc.sms + (e.sms_this_month || 0),
      email: acc.email + (e.email_this_month || 0),
      vols: acc.vols + (e.volunteers_this_month || 0),
      events: acc.events + 1,
    }), { sms: 0, email: 0, vols: 0, events: 0 });
  }, [overview]);

  if (!user || !user.is_super_admin) return null;

  const monthLabel = overview?.month_start ? new Date(overview.month_start).toLocaleDateString("en-AU", { month: "long", year: "numeric" }) : "";

  return (
    <AdminShell title="Super Admin" contentClassName="bg-[#0B0B0F] text-white min-h-[calc(100vh-3.5rem)]">
      <main className="mx-auto max-w-7xl px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#FFD700]">Command centre</div>
            <h1 className="mt-1 font-display text-4xl font-black tracking-tighter md:text-5xl">Every event, every organiser.</h1>
            <p className="mt-2 text-sm text-zinc-400">Ultimate access — every action you take on someone else's event is logged. {monthLabel ? <>Metrics tagged "This month" reset on the 1st of {monthLabel}.</> : null}</p>
          </div>
          <button onClick={load} className="inline-flex items-center gap-1.5 border border-white/20 px-3 py-1.5 text-xs hover:border-[#FFD700]" data-testid="sa-refresh">
            <RefreshCcw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {/* Aggregate chip row */}
        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          <SaChip label="Live events" value={totals.events} icon={<Calendar className="h-4 w-4" />} />
          <SaChip label="SMS this month" value={totals.sms} icon={<MessageSquare className="h-4 w-4" />} />
          <SaChip label="Emails this month" value={totals.email} icon={<Mail className="h-4 w-4" />} />
          <SaChip label="Volunteers this month" value={totals.vols} icon={<Users className="h-4 w-4" />} />
        </div>

        {/* Tabs */}
        <div className="mt-8 inline-flex border border-[#FFD700]/30">
          <button onClick={() => setTab("events")} data-testid="sa-tab-events" className={`px-4 py-1.5 text-xs font-black uppercase tracking-widest ${tab === "events" ? "bg-[#FFD700] text-black" : "text-zinc-300 hover:text-white"}`}>Events</button>
          <button onClick={() => setTab("organisers")} data-testid="sa-tab-organisers" className={`px-4 py-1.5 text-xs font-black uppercase tracking-widest ${tab === "organisers" ? "bg-[#FFD700] text-black" : "text-zinc-300 hover:text-white"}`}>Organisers</button>
        </div>

        {tab === "events" ? (
          <div className="mt-4 overflow-x-auto border border-white/10">
            <table className="min-w-full text-xs" data-testid="sa-events-table">
              <thead className="bg-[#12121A]">
                <tr className="text-left uppercase tracking-widest text-[10px] text-zinc-400">
                  <th className="px-3 py-2">Event</th>
                  <th className="px-3 py-2">Owner</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">SMS (mo / total)</th>
                  <th className="px-3 py-2 text-right">Email (mo / total)</th>
                  <th className="px-3 py-2 text-right">Vols (mo / total)</th>
                  <th className="px-3 py-2 text-right">Manage</th>
                </tr>
              </thead>
              <tbody>
                {(overview?.events || []).map((e) => (
                  <tr key={e.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`sa-event-row-${e.id}`}>
                    <td className="px-3 py-2 font-semibold">{e.name}</td>
                    <td className="px-3 py-2 text-zinc-400">{e.owner_name || "—"}<br /><span className="font-mono text-[10px]">{e.owner_email}</span></td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${e.archived ? "bg-zinc-700 text-zinc-200" : e.status === "published" ? "bg-emerald-700 text-white" : "bg-zinc-800 text-zinc-300"}`}>
                        {e.archived ? "Archived" : e.status || "draft"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{e.sms_this_month} <span className="text-zinc-500">/ {e.sms_total}</span></td>
                    <td className="px-3 py-2 text-right font-mono">{e.email_this_month} <span className="text-zinc-500">/ {e.email_total}</span></td>
                    <td className="px-3 py-2 text-right font-mono">{e.volunteers_this_month} <span className="text-zinc-500">/ {e.volunteers_total}</span></td>
                    <td className="px-3 py-2 text-right">
                      <Link to={`/admin/events/${e.id}`} data-testid={`sa-manage-${e.id}`} className="inline-flex items-center gap-1 border border-[#FFD700] px-2 py-1 text-[10px] font-black uppercase tracking-widest text-[#FFD700] hover:bg-[#FFD700] hover:text-black">
                        Manage <ExternalLink className="h-3 w-3" />
                      </Link>
                    </td>
                  </tr>
                ))}
                {(overview?.events || []).length === 0 && !loading ? (
                  <tr><td colSpan={7} className="px-3 py-8 text-center italic text-zinc-500">No events on the platform yet.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-4">
            <div className="flex items-center gap-2 border border-white/10 bg-[#12121A] px-3 py-2">
              <Search className="h-4 w-4 text-zinc-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search organisers by name or email…" className="flex-1 bg-transparent text-sm outline-none placeholder-zinc-500" data-testid="sa-organiser-search" />
            </div>
            <div className="mt-3 overflow-x-auto border border-white/10">
              <table className="min-w-full text-xs" data-testid="sa-organisers-table">
                <thead className="bg-[#12121A]">
                  <tr className="text-left uppercase tracking-widest text-[10px] text-zinc-400">
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2 text-right">Live</th>
                    <th className="px-3 py-2 text-right">Past</th>
                    <th className="px-3 py-2 text-right">Archived</th>
                    <th className="px-3 py-2 text-right">Volunteers</th>
                    <th className="px-3 py-2 text-right">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.map((o) => (
                    <tr key={o.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`sa-org-row-${o.id}`}>
                      <td className="px-3 py-2 font-semibold">{o.name || "—"} {o.is_super_admin ? <span className="ml-1 text-[9px] font-black uppercase tracking-widest text-[#FFD700]">SA</span> : null}</td>
                      <td className="px-3 py-2 font-mono text-zinc-400">{o.email}</td>
                      <td className="px-3 py-2 text-right font-mono">{o.live_events}</td>
                      <td className="px-3 py-2 text-right font-mono">{o.past_events}</td>
                      <td className="px-3 py-2 text-right font-mono">{o.archived_events}</td>
                      <td className="px-3 py-2 text-right font-mono">{o.total_volunteers}</td>
                      <td className="px-3 py-2 text-right font-mono text-zinc-500">{o.created_at ? new Date(o.created_at).toLocaleDateString("en-AU") : ""}</td>
                    </tr>
                  ))}
                  {orgs.length === 0 && !loading ? (
                    <tr><td colSpan={7} className="px-3 py-8 text-center italic text-zinc-500">No organisers match "{search}".</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </AdminShell>
  );
}

function SaChip({ label, value, icon }) {
  return (
    <div className="border border-[#FFD700]/30 bg-[#12121A] px-4 py-3">
      <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[#FFD700]">
        {icon} {label}
      </div>
      <div className="mt-1 font-display text-3xl font-black tracking-tighter">{value}</div>
    </div>
  );
}
